// components/AIAssistantForm.jsx
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const AIAssistantForm = ({ providerData }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatAreaRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTo({
        top: chatAreaRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setLoading(true);
    setInput("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/providers/ai-chat/${providerData.provider.id}`,
        {
          userMessage: input,
          providerData,
        }
      );
      setMessages([
        ...newMessages,
        { role: "assistant", content: res.data.reply },
      ]);
    } catch (err) {
      console.error("AI response error", err);
      setMessages([
        ...newMessages,
        { role: "assistant", content: "Sorry, something went wrong." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="bg-[#2F2F2F] rounded-3xl shadow-xl shadow-black/25">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 rounded-t-3xl border-b border-gray-600">
        <div className="flex items-center gap-3">
          {providerData?.provider?.profilePicUrl ? (
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-500 flex items-center justify-center flex-shrink-0">
              <img 
                src={providerData.provider.profilePicUrl} 
                alt={`${providerData?.provider?.providerUsername || 'Provider'} profile`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
              <span className="text-xl text-white font-bold">
                {providerData?.provider?.providerUsername?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-white">
                {providerData?.provider?.businessName || providerData?.provider?.providerUsername || 'Provider'}'s Assistant
              </h3>
              <span className="inline-block w-2 h-2 bg-purple-400 rounded-full animate-pulse" title="Online"></span>
            </div>
            <span className="text-xs text-gray-400">Powered by <span className="font-semibold text-purple-400">UHyve AI</span></span>
          </div>
        </div>
        <button
          onClick={clearChat}
          className="text-gray-300 hover:text-white p-2 rounded-lg transition-colors"
          aria-label="Clear Chat"
          title="Clear chat history"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Chat Area */}
      <div 
        ref={chatAreaRef}
        className="overflow-y-auto px-4 py-3 space-y-3"
        style={{ 
          height: 'clamp(300px, 60vh, 500px)', // Responsive height
          scrollbarWidth: 'thin', 
          scrollbarColor: '#a78bfa #f3e8ff' 
        }}
      >
        {messages.length === 0 && !loading && (
          <div className="text-center text-gray-400 text-sm mt-8">
            <div className="mb-2">
              <svg className="w-8 h-8 mx-auto text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            Ask questions about services, availability, or anything else!
          </div>
        )}
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`relative max-w-[80%] px-3 py-2 text-sm whitespace-pre-line break-words mb-1
                ${msg.role === "user"
                  ? "bg-purple-600 text-white rounded-2xl rounded-br-md ml-4"
                  : "bg-gray-700 text-white rounded-2xl rounded-bl-md mr-4"}
              `}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-700 text-white px-3 py-2 rounded-2xl rounded-bl-md text-xs animate-pulse mr-4">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-center px-4 py-3 rounded-b-3xl border-t border-gray-600 gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 border border-gray-500 px-3 py-2 rounded-l-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400 transition-colors bg-gray-700 placeholder-gray-400"
          placeholder="Ask a question..."
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          disabled={loading}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className={`bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-r-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default AIAssistantForm;






